// frontend/src/portal/pages/OpsAuditLog.jsx
import { useCallback, useEffect, useMemo, useState } from "react";
import ErrorBoundary from "../components/ErrorBoundary.jsx";
import { fetchAuditLog } from "../api.js";
import { useImpersonation } from "../hooks/useImpersonation.jsx";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function MetadataBlock({ data }) {
  if (data == null) {
    return <span style={{ color: "var(--text-muted)" }}>—</span>;
  }
  let formatted = "";
  if (typeof data === "string") {
    formatted = data;
  } else {
    try {
      formatted = JSON.stringify(data, null, 2);
    } catch {
      formatted = String(data);
    }
  }
  return (
    <pre
      style={{
        margin: 0,
        fontSize: "12px",
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        maxWidth: "300px",
      }}>
      {formatted}
    </pre>
  );
}

function formatName(session) {
  const parts = [session?.firstName, session?.lastName].filter(Boolean);
  if (parts.length) return parts.join(" ");
  if (session?.email) return session.email;
  if (session?.userId) return `User ${session.userId}`;
  return "Unknown";
}

export default function OpsAuditLog() {
  const { impersonating, session, startImpersonation, stopImpersonation, updating } = useImpersonation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [entries, setEntries] = useState([]);
  const [limit, setLimit] = useState(100);
  const [tenantFilter, setTenantFilter] = useState("");
  const [refreshToken, setRefreshToken] = useState(0);
  const [startTenantId, setStartTenantId] = useState("");
  const [startUserId, setStartUserId] = useState("");
  const [starting, setStarting] = useState(false);

  const loadAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      const parsedLimit = Number(limit);
      if (Number.isFinite(parsedLimit) && parsedLimit > 0) {
        params.limit = parsedLimit;
      }
      const parsedTenant = Number(tenantFilter);
      if (Number.isFinite(parsedTenant) && parsedTenant > 0) {
        params.tenantId = parsedTenant;
      }
      const data = await fetchAuditLog(params);
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
    } catch (err) {
      setError(err?.message || "Failed to load audit log");
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [limit, tenantFilter]);

  useEffect(() => {
    loadAudit().catch(() => {});
  }, [loadAudit, refreshToken]);

  const handleRefresh = useCallback((event) => {
    event?.preventDefault?.();
    setRefreshToken((token) => token + 1);
  }, []);

  const handleStart = async (event) => {
    event.preventDefault();
    const tenantId = Number(startTenantId);
    const userId = Number(startUserId);
    if (!Number.isFinite(tenantId) || tenantId <= 0) {
      setError("Enter a valid tenant ID to impersonate.");
      return;
    }
    if (!Number.isFinite(userId) || userId <= 0) {
      setError("Enter a valid user ID to impersonate.");
      return;
    }
    setError(null);
    setStarting(true);
    try {
      await startImpersonation(tenantId, userId);
    } catch (err) {
      setError(err?.message || "Failed to start impersonation");
    } finally {
      setStarting(false);
    }
  };

  const handleStop = async () => {
    setError(null);
    try {
      await stopImpersonation();
    } catch (err) {
      setError(err?.message || "Failed to stop impersonation");
    }
  };

  const currentSession = useMemo(() => {
    if (!impersonating || !session) return null;
    return session;
  }, [impersonating, session]);

  return (
    <ErrorBoundary componentName="OpsAuditLogPage">
      <div style={{ minHeight: "100vh", background: "var(--bg-secondary)" }}>
        {/* Header */}
        <div style={{ background: "var(--accent-gradient)", borderBottom: "1px solid var(--border-secondary)" }}>
          <div style={{ maxWidth: "var(--container-max-wide)", margin: "0 auto", padding: "var(--space-2xl) var(--space-xl)" }}>
            <h1 style={{ fontSize: "32px", fontWeight: "400", color: "#fff", margin: "0 0 var(--space-sm) 0", letterSpacing: "-0.5px" }}>Audit Log</h1>
            <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.9)", margin: 0 }}>System activity tracking and impersonation controls</p>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ maxWidth: "var(--container-max-wide)", margin: "0 auto", padding: "var(--space-2xl) var(--space-xl)" }}>
          {/* Impersonation Section */}
          <div
            style={{
              backgroundColor: "var(--surface)",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--border-primary)",
              padding: "var(--space-xl)",
              marginBottom: "var(--space-2xl)",
              boxShadow: "var(--shadow-sm)",
            }}>
            <h2 style={{ fontSize: "18px", fontWeight: "600", color: "var(--text-primary)", margin: "0 0 var(--space-lg) 0" }}>Impersonation Controls</h2>

            {currentSession ? (
              <div>
                <div
                  style={{
                    padding: "var(--space-lg)",
                    backgroundColor: "var(--warning-bg)",
                    border: "1px solid var(--warning)",
                    borderRadius: "var(--radius-md)",
                    marginBottom: "var(--space-lg)",
                  }}>
                  <p style={{ margin: 0, color: "var(--warning)", fontSize: "14px" }}>
                    Currently impersonating <strong>{formatName(currentSession)}</strong>
                    {currentSession.email ? ` (${currentSession.email})` : ""}
                    {currentSession.tenantName || currentSession.tenantSlug ? ` — Tenant ${currentSession.tenantName || currentSession.tenantSlug}` : ""}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleStop}
                  disabled={updating}
                  style={{
                    padding: "var(--space-md) var(--space-xl)",
                    backgroundColor: "var(--danger)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "var(--radius-md)",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: updating ? "not-allowed" : "pointer",
                  }}>
                  {updating ? "Stopping…" : "Stop Impersonation"}
                </button>
              </div>
            ) : (
              <form onSubmit={handleStart} style={{ display: "grid", gap: "var(--space-lg)", maxWidth: "500px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "var(--text-primary)", marginBottom: "var(--space-xs)" }}>Tenant ID</label>
                  <input
                    type="number"
                    value={startTenantId}
                    onChange={(e) => setStartTenantId(e.target.value)}
                    min="1"
                    required
                    style={{
                      width: "100%",
                      padding: "var(--space-md)",
                      border: "1px solid var(--border-primary)",
                      borderRadius: "var(--radius-md)",
                      fontSize: "14px",
                      backgroundColor: "var(--surface)",
                      color: "var(--text-primary)",
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "var(--text-primary)", marginBottom: "var(--space-xs)" }}>User ID</label>
                  <input
                    type="number"
                    value={startUserId}
                    onChange={(e) => setStartUserId(e.target.value)}
                    min="1"
                    required
                    style={{
                      width: "100%",
                      padding: "var(--space-md)",
                      border: "1px solid var(--border-primary)",
                      borderRadius: "var(--radius-md)",
                      fontSize: "14px",
                      backgroundColor: "var(--surface)",
                      color: "var(--text-primary)",
                    }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={starting}
                  style={{
                    padding: "var(--space-md) var(--space-xl)",
                    backgroundColor: starting ? "var(--text-disabled)" : "var(--accent-primary)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "var(--radius-md)",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: starting ? "not-allowed" : "pointer",
                    width: "fit-content",
                  }}>
                  {starting ? "Starting…" : "Start Impersonation"}
                </button>
              </form>
            )}
          </div>

          {/* Filters */}
          <div
            style={{
              backgroundColor: "var(--surface)",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--border-primary)",
              padding: "var(--space-xl)",
              marginBottom: "var(--space-2xl)",
              boxShadow: "var(--shadow-sm)",
            }}>
            <h2 style={{ fontSize: "18px", fontWeight: "600", color: "var(--text-primary)", margin: "0 0 var(--space-lg) 0" }}>Filters</h2>

            <form onSubmit={handleRefresh} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "var(--space-md)", alignItems: "end" }}>
              <div>
                <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "var(--text-primary)", marginBottom: "var(--space-xs)" }}>Limit</label>
                <input
                  type="number"
                  min="1"
                  max="500"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "var(--space-md)",
                    border: "1px solid var(--border-primary)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "14px",
                    backgroundColor: "var(--surface)",
                    color: "var(--text-primary)",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "var(--text-primary)", marginBottom: "var(--space-xs)" }}>Tenant ID</label>
                <input
                  type="number"
                  min="1"
                  value={tenantFilter}
                  onChange={(e) => setTenantFilter(e.target.value)}
                  placeholder="All tenants"
                  style={{
                    width: "100%",
                    padding: "var(--space-md)",
                    border: "1px solid var(--border-primary)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "14px",
                    backgroundColor: "var(--surface)",
                    color: "var(--text-primary)",
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: "var(--space-md) var(--space-xl)",
                  backgroundColor: loading ? "var(--text-disabled)" : "var(--accent-primary)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "var(--radius-md)",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: loading ? "not-allowed" : "pointer",
                }}>
                {loading ? "Loading…" : "Refresh"}
              </button>
            </form>
          </div>

          {error && (
            <div
              style={{
                backgroundColor: "var(--danger-bg)",
                border: "1px solid var(--danger)",
                borderRadius: "var(--radius-md)",
                padding: "var(--space-lg)",
                marginBottom: "var(--space-xl)",
              }}>
              <p style={{ color: "var(--danger)", margin: 0, fontSize: "14px" }}>{error}</p>
            </div>
          )}

          {/* Audit Log Table */}
          <div
            style={{
              backgroundColor: "var(--surface)",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--border-primary)",
              overflow: "hidden",
              boxShadow: "var(--shadow-sm)",
            }}>
            <div style={{ padding: "var(--space-xl)", borderBottom: "1px solid var(--border-primary)" }}>
              <h2 style={{ fontSize: "18px", fontWeight: "600", color: "var(--text-primary)", margin: 0 }}>Audit Entries {entries.length > 0 && `(${entries.length})`}</h2>
            </div>

            <div style={{ overflowX: "auto" }}>
              {loading ? (
                <div style={{ padding: "var(--space-2xl)", textAlign: "center" }}>
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      border: "3px solid var(--border-primary)",
                      borderTopColor: "var(--accent-primary)",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                      margin: "0 auto var(--space-lg)",
                    }}
                  />
                  <p style={{ color: "var(--text-muted)" }}>Loading audit log…</p>
                </div>
              ) : entries.length === 0 ? (
                <div style={{ padding: "var(--space-2xl)", textAlign: "center" }}>
                  <p style={{ color: "var(--text-muted)", margin: 0 }}>No audit entries found.</p>
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "var(--surface-elevated)", borderBottom: "1px solid var(--border-primary)" }}>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>ID</th>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Tenant</th>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Actor</th>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Action</th>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Target</th>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Metadata</th>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>IP</th>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>User Agent</th>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry) => (
                      <tr key={entry.id} style={{ borderBottom: "1px solid var(--border-primary)" }}>
                        <td style={{ padding: "var(--space-md)", fontSize: "14px", color: "var(--text-primary)" }}>{entry.id}</td>
                        <td style={{ padding: "var(--space-md)", fontSize: "14px", color: "var(--text-primary)" }}>
                          {entry.tenantName || entry.tenantId || "—"}
                          {entry.tenantId && <span style={{ color: "var(--text-muted)", fontSize: "12px" }}> (#{entry.tenantId})</span>}
                        </td>
                        <td style={{ padding: "var(--space-md)", fontSize: "14px", color: "var(--text-primary)" }}>{entry.actorUserId ? `#${entry.actorUserId}` : "—"}</td>
                        <td style={{ padding: "var(--space-md)", fontSize: "14px", color: "var(--text-primary)", fontFamily: "monospace" }}>{entry.action}</td>
                        <td style={{ padding: "var(--space-md)", fontSize: "14px", color: "var(--text-primary)" }}>
                          {entry.targetType}
                          {entry.targetId && <span style={{ color: "var(--text-muted)", fontSize: "12px" }}> #{entry.targetId}</span>}
                        </td>
                        <td style={{ padding: "var(--space-md)", fontSize: "12px" }}>
                          <MetadataBlock data={entry.metadata} />
                        </td>
                        <td style={{ padding: "var(--space-md)", color: "var(--text-muted)", fontFamily: "monospace", fontSize: "12px" }}>{entry.ip || "—"}</td>
                        <td style={{ padding: "var(--space-md)", fontSize: "12px", color: "var(--text-muted)", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {entry.userAgent || "—"}
                        </td>
                        <td style={{ padding: "var(--space-md)", fontSize: "14px", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{formatDate(entry.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      </div>
    </ErrorBoundary>
  );
}
